import { useState, useEffect, useMemo } from "react";
import axios from "axios";

import RotateDevice from "../../../components/RotateDevice/RotateDevice";
import "../Generale/Generale.css";
import "../../HrGenerale/TrendKpi/TrendKpi.css";
import Lf23Loader from "../../../components/Lf23Loader";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

// ── Helpers di formattazione (stessi di Generale.js) ──

const fmtNum = (v) => {
    if (v == null) return "—";
    return v.toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

const fmtEuro = (v) => {
    if (v == null) return "—";
    return v.toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " €";
};

const fmtPerc = (v) => {
    if (v == null) return "—";
    return v.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + "%";
};

const getTrimestre = (mese) => Math.ceil(mese / 3);

const MESI_FULL = ['', 'GENNAIO', 'FEBBRAIO', 'MARZO', 'APRILE', 'MAGGIO', 'GIUGNO', 'LUGLIO', 'AGOSTO', 'SETTEMBRE', 'OTTOBRE', 'NOVEMBRE', 'DICEMBRE'];

// ── Componente ──

const Dettaglio = ({ filters }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);

    // Ogni tabella ha il suo toggle M/T/A, fullscreen, e stato expand
    const [tableOreViewType, setTableOreViewType] = useState('anno');
    const [tableKpiViewType, setTableKpiViewType] = useState('anno');
    const [expandedOre, setExpandedOre] = useState(new Set());
    const [expandedKpi, setExpandedKpi] = useState(new Set());
    const [isFullscreenOre, setIsFullscreenOre] = useState(false);
    const [isFullscreenKpi, setIsFullscreenKpi] = useState(false);

    // ESC per uscire da fullscreen
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                setIsFullscreenOre(false);
                setIsFullscreenKpi(false);
            }
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, []);

    // ── Fetch dati ──
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const params = {
                    fromDate: filters.fromDate || "",
                    toDate: filters.toDate || "",
                    settore: filters.settore?.map(s => s.value).join(",") || "",
                    tipContratto: filters.tipContratto?.map(t => t.value).join(",") || "",
                    commessa: filters.commessa?.map(c => c.value).join(",") || "",
                    livello1: filters.livello1?.map(l => l.value).join(",") || "",
                    livello2: filters.livello2?.map(l => l.value).join(",") || "",
                    livello3: filters.livello3?.map(l => l.value).join(",") || "",
                    livello4: filters.livello4?.map(l => l.value).join(",") || "",
                };
                const res = await axios.get(`${API_URL}/ambiente/dettaglio`, { params });
                setData(res.data.dettaglioData || []);
            } catch (error) {
                console.error("Errore nel caricamento dati Dettaglio:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [filters]);

    const toggleOre = (settore) => {
        setExpandedOre(prev => {
            const next = new Set(prev);
            if (next.has(settore)) next.delete(settore); else next.add(settore);
            return next;
        });
    };

    const toggleKpi = (settore) => {
        setExpandedKpi(prev => {
            const next = new Set(prev);
            if (next.has(settore)) next.delete(settore); else next.add(settore);
            return next;
        });
    };

    // ════════════════════════════════════════════
    // Builder generico per tableData (riuso per entrambe le tabelle)
    // ════════════════════════════════════════════
    // Prende i dati raw e il viewType, restituisce la stessa struttura
    // di tableData usata in Generale.js: periods, periodLabels, anniMap,
    // trimGroups, settori, aggregateByPeriod(rows)

    const buildTableData = (rawData, viewType, aggregateFn) => {
        if (!rawData || rawData.length === 0) return { periods: [], settori: {} };

        const periodsSet = new Set();
        rawData.forEach(r => {
            if (viewType === 'mese') periodsSet.add(`${r.anno}-${r.mese_num}`);
            else if (viewType === 'trimestre') periodsSet.add(`${r.anno}-T${getTrimestre(r.mese_num)}`);
            else periodsSet.add(`${r.anno}`);
        });

        const periods = Array.from(periodsSet).sort((a, b) => {
            const pA = a.split('-'), pB = b.split('-');
            const annoA = parseInt(pA[0]), annoB = parseInt(pB[0]);
            if (annoA !== annoB) return annoA - annoB;
            if (pA.length === 1) return 0;
            const subA = pA[1].startsWith('T') ? parseInt(pA[1].slice(1)) : parseInt(pA[1]);
            const subB = pB[1].startsWith('T') ? parseInt(pB[1].slice(1)) : parseInt(pB[1]);
            return subA - subB;
        });

        const periodLabels = periods.map(p => {
            const parts = p.split('-');
            if (viewType === 'mese') return MESI_FULL[parseInt(parts[1])];
            if (viewType === 'trimestre') return `Trim ${parts[1].slice(1)}`;
            return parts[0];
        });

        const getPeriodKey = (r) => {
            if (viewType === 'mese') return `${r.anno}-${r.mese_num}`;
            if (viewType === 'trimestre') return `${r.anno}-T${getTrimestre(r.mese_num)}`;
            return `${r.anno}`;
        };

        const anniMap = {};
        periods.forEach((p, i) => {
            const anno = p.split('-')[0];
            if (!anniMap[anno]) anniMap[anno] = [];
            anniMap[anno].push(i);
        });

        const trimGroups = [];
        if (viewType === 'mese') {
            const trimMap = {};
            periods.forEach((p, i) => {
                const [anno, meseStr] = p.split('-');
                const trim = getTrimestre(parseInt(meseStr));
                const key = `${anno}-T${trim}`;
                if (!trimMap[key]) trimMap[key] = { anno, trim, label: `TRIM ${trim}`, key, monthIndices: [] };
                trimMap[key].monthIndices.push(i);
            });
            Object.keys(trimMap).sort().forEach(k => trimGroups.push(trimMap[k]));
        }

        const settori = {};
        rawData.forEach(r => {
            if (!settori[r.desc_settore]) settori[r.desc_settore] = { commesse: {}, allRows: [] };
            settori[r.desc_settore].allRows.push(r);
            if (!settori[r.desc_settore].commesse[r.desc_commessa]) {
                settori[r.desc_settore].commesse[r.desc_commessa] = [];
            }
            settori[r.desc_settore].commesse[r.desc_commessa].push(r);
        });

        // aggregateByPeriod: dato un array di righe, somma i valori grezzi per periodo
        // poi chiama aggregateFn per calcolare i KPI specifici della tabella
        const aggregateByPeriod = (rows) => {
            const perPeriod = {};
            rows.forEach(r => {
                const key = getPeriodKey(r);
                if (!perPeriod[key]) perPeriod[key] = { ore_tot: 0, ore_lav: 0, ore_pei: 0, tot_costo: 0, tot_ricavo: 0 };
                perPeriod[key].ore_tot += r.ore_tot;
                perPeriod[key].ore_lav += r.ore_lav;
                perPeriod[key].ore_pei += r.ore_pei;
                perPeriod[key].tot_costo += r.tot_costo;
                perPeriod[key].tot_ricavo += r.tot_ricavo;
            });

            const totale = { ore_tot: 0, ore_lav: 0, ore_pei: 0, tot_costo: 0, tot_ricavo: 0 };
            Object.values(perPeriod).forEach(v => {
                totale.ore_tot += v.ore_tot;
                totale.ore_lav += v.ore_lav;
                totale.ore_pei += v.ore_pei;
                totale.tot_costo += v.tot_costo;
                totale.tot_ricavo += v.tot_ricavo;
            });

            const kpiPerPeriod = periods.map(p => {
                const d = perPeriod[p] || { ore_tot: 0, ore_lav: 0, ore_pei: 0, tot_costo: 0, tot_ricavo: 0 };
                return aggregateFn(d);
            });

            const kpiPerTrimestre = {};
            trimGroups.forEach(tg => {
                const trimData = { ore_tot: 0, ore_lav: 0, ore_pei: 0, tot_costo: 0, tot_ricavo: 0 };
                tg.monthIndices.forEach(i => {
                    const d = perPeriod[periods[i]] || { ore_tot: 0, ore_lav: 0, ore_pei: 0, tot_costo: 0, tot_ricavo: 0 };
                    trimData.ore_tot += d.ore_tot;
                    trimData.ore_lav += d.ore_lav;
                    trimData.ore_pei += d.ore_pei;
                    trimData.tot_costo += d.tot_costo;
                    trimData.tot_ricavo += d.tot_ricavo;
                });
                kpiPerTrimestre[tg.key] = aggregateFn(trimData);
            });

            const kpiTotale = aggregateFn(totale);
            return { kpiPerPeriod, kpiPerTrimestre, kpiTotale };
        };

        return { periods, periodLabels, anniMap, trimGroups, settori, aggregateByPeriod };
    };

    // ── Funzioni di calcolo KPI per le due tabelle ──

    // Tabella 1 "Dettaglio Ore": Ore Tot, Ore Lav, Ore PEI, Diff [%]
    const oreAggregateFn = (d) => ({
        ore_tot: d.ore_tot > 0 ? d.ore_tot : null,
        ore_lav: d.ore_lav > 0 ? d.ore_lav : null,
        ore_pei: d.ore_pei > 0 ? d.ore_pei : null,
        diff_perc: d.ore_tot > 0 ? ((d.ore_tot - d.ore_pei) / d.ore_tot) * 100 : null,
    });

    // Tabella 2 "KPI per Settore": Ore Tot, Costi, Costo [%], Ricavi
    const kpiAggregateFn = (d) => ({
        ore_tot: d.ore_tot > 0 ? d.ore_tot : null,
        costi: d.tot_costo > 0 ? d.tot_costo : null,
        costo_perc: d.tot_ricavo > 0 ? (d.tot_costo / d.tot_ricavo) * 100 : null,
        ricavi: d.tot_ricavo > 0 ? d.tot_ricavo : null,
    });

    // ── tableData per ciascuna tabella ──
    const tableOreData = useMemo(() => buildTableData(data, tableOreViewType, oreAggregateFn), [data, tableOreViewType]);
    const tableKpiData = useMemo(() => buildTableData(data, tableKpiViewType, kpiAggregateFn), [data, tableKpiViewType]);

    const globalOreKpi = useMemo(() => {
        if (!tableOreData.aggregateByPeriod || data.length === 0) return null;
        return tableOreData.aggregateByPeriod(data);
    }, [data, tableOreData]);

    const globalKpiKpi = useMemo(() => {
        if (!tableKpiData.aggregateByPeriod || data.length === 0) return null;
        return tableKpiData.aggregateByPeriod(data);
    }, [data, tableKpiData]);

    // ════════════════════════════════════════════
    // Render helpers generici (riuso per entrambe le tabelle)
    // ════════════════════════════════════════════

    // Render celle KPI per una riga
    const renderCells = (aggResult, tData, subCols, formatters) => {
        const { kpiPerPeriod, kpiPerTrimestre, kpiTotale } = aggResult;
        const cells = [];

        if (tData.trimGroups && tData.trimGroups.length > 0) {
            // Vista mensile
            tData.trimGroups.forEach(tg => {
                tg.monthIndices.forEach(i => {
                    const kpi = kpiPerPeriod[i];
                    subCols.forEach((col, si) => {
                        cells.push(<td key={`${col}-${i}`}>{formatters[si](kpi[col])}</td>);
                    });
                });
                // TOTALE del trimestre
                const trimKpi = kpiPerTrimestre[tg.key] || {};
                subCols.forEach((col, si) => {
                    cells.push(<td key={`t-${col}-${tg.key}`} className="amb-kpi-td-totale">{formatters[si](trimKpi[col])}</td>);
                });
            });
        } else {
            // Vista trimestrale/annuale
            kpiPerPeriod.forEach((kpi, i) => {
                subCols.forEach((col, si) => {
                    cells.push(<td key={`${col}-${i}`}>{formatters[si](kpi[col])}</td>);
                });
            });
            // Totale globale
            subCols.forEach((col, si) => {
                cells.push(<td key={`tot-${col}`} className="amb-kpi-td-totale">{formatters[si](kpiTotale[col])}</td>);
            });
        }
        return cells;
    };

    // Render header multi-livello (identico a Generale.js)
    const renderTableHeaders = (tData, viewType, subColLabels) => {
        const { periods, periodLabels, anniMap } = tData;
        if (!periods || periods.length === 0) return null;

        const headerRows = [];
        const anniKeys = Object.keys(anniMap).sort();

        if (viewType === 'anno') {
            const row1 = [<th key="corner" className="amb-kpi-th-corner">Anno</th>];
            anniKeys.forEach(anno => {
                row1.push(<th key={`a-${anno}`} colSpan={subColLabels.length}>{anno}</th>);
            });
            row1.push(<th key="a-tot" colSpan={subColLabels.length} className="amb-kpi-th-totale">Totale</th>);
            headerRows.push(<tr key="h0">{row1}</tr>);

            const row2 = [<th key="corner2" className="amb-kpi-th-corner">Desc Settore</th>];
            for (let i = 0; i < anniKeys.length + 1; i++) {
                const isTotale = i === anniKeys.length;
                subColLabels.forEach((sub, si) => {
                    row2.push(<th key={`sub-${i}-${si}`} className={isTotale ? 'amb-kpi-th-totale' : ''}>{sub}</th>);
                });
            }
            headerRows.push(<tr key="h1">{row2}</tr>);

        } else if (viewType === 'trimestre') {
            const row1 = [<th key="corner" className="amb-kpi-th-corner">Anno</th>];
            anniKeys.forEach(anno => {
                const count = anniMap[anno].length;
                if (count === 0) return;
                row1.push(<th key={`a-${anno}`} colSpan={count * subColLabels.length}>{anno}</th>);
            });
            row1.push(<th key="a-tot" colSpan={subColLabels.length} className="amb-kpi-th-totale">Totale</th>);
            headerRows.push(<tr key="h0">{row1}</tr>);

            const row2 = [<th key="corner2" className="amb-kpi-th-corner">Trimestre</th>];
            periods.forEach((_, i) => {
                row2.push(<th key={`t-${i}`} colSpan={subColLabels.length}>{periodLabels[i]}</th>);
            });
            headerRows.push(<tr key="h1">{row2}</tr>);

            const row3 = [<th key="corner3" className="amb-kpi-th-corner">Desc Settore</th>];
            periods.forEach((_, i) => {
                subColLabels.forEach((sub, si) => {
                    row3.push(<th key={`sub-${i}-${si}`}>{sub}</th>);
                });
            });
            subColLabels.forEach((sub, si) => {
                row3.push(<th key={`sub-tot-${si}`} className="amb-kpi-th-totale">{sub}</th>);
            });
            headerRows.push(<tr key="h2">{row3}</tr>);

        } else {
            // Vista mensile: 4 righe
            const { trimGroups: tGroups } = tData;

            const row1 = [<th key="c1" className="amb-kpi-th-corner">ANNO</th>];
            anniKeys.forEach(anno => {
                const trimsInYear = tGroups.filter(tg => tg.anno === anno);
                let colSpan = 0;
                trimsInYear.forEach(tg => {
                    colSpan += tg.monthIndices.length * subColLabels.length + subColLabels.length;
                });
                if (colSpan === 0) return;
                row1.push(<th key={`a-${anno}`} colSpan={colSpan}>{anno}</th>);
            });
            headerRows.push(<tr key="h0">{row1}</tr>);

            const row2 = [<th key="c2" className="amb-kpi-th-corner">TRIMESTRE</th>];
            tGroups.forEach(tg => {
                const colSpan = tg.monthIndices.length * subColLabels.length + subColLabels.length;
                row2.push(<th key={`t-${tg.key}`} colSpan={colSpan}>{tg.label}</th>);
            });
            headerRows.push(<tr key="h1">{row2}</tr>);

            const row3 = [<th key="c3" className="amb-kpi-th-corner">Mese</th>];
            tGroups.forEach(tg => {
                tg.monthIndices.forEach(idx => {
                    row3.push(<th key={`m-${idx}`} colSpan={subColLabels.length}>{periodLabels[idx]}</th>);
                });
                row3.push(<th key={`mt-${tg.key}`} colSpan={subColLabels.length} className="amb-kpi-th-totale">TOTALE</th>);
            });
            headerRows.push(<tr key="h2">{row3}</tr>);

            const row4 = [<th key="c4" className="amb-kpi-th-corner">Desc Settore</th>];
            tGroups.forEach(tg => {
                tg.monthIndices.forEach(idx => {
                    subColLabels.forEach((sub, si) => {
                        row4.push(<th key={`s-${idx}-${si}`}>{sub}</th>);
                    });
                });
                subColLabels.forEach((sub, si) => {
                    row4.push(<th key={`st-${tg.key}-${si}`} className="amb-kpi-th-totale">{sub}</th>);
                });
            });
            headerRows.push(<tr key="h3">{row4}</tr>);
        }
        return headerRows;
    };

    // Render body generico per una tabella gerarchica
    const renderTableBody = (tData, globalKpi, expandedSet, toggleFn, subCols, formatters) => {
        if (data.length === 0 || !tData.settori) {
            return (
                <tr>
                    <td colSpan={100} className="amb-kpi-cell-empty">Nessun dato disponibile</td>
                </tr>
            );
        }

        return (
            <>
                {Object.keys(tData.settori).sort().map(settore => {
                    const sData = tData.settori[settore];
                    const isExpanded = expandedSet.has(settore);
                    const settoreAgg = tData.aggregateByPeriod(sData.allRows);
                    const commesseNames = Object.keys(sData.commesse).sort();

                    return [
                        <tr key={settore} className="amb-kpi-row-settore" onClick={() => toggleFn(settore)}>
                            <td className="amb-kpi-cell-label">
                                <span className="amb-kpi-toggle">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className={isExpanded ? "expanded" : ""}>
                                        <polyline points="9 18 15 12 9 6"></polyline>
                                    </svg>
                                    {settore}
                                </span>
                            </td>
                            {renderCells(settoreAgg, tData, subCols, formatters)}
                        </tr>,
                        ...(isExpanded ? commesseNames.map(comm => {
                            const commAgg = tData.aggregateByPeriod(sData.commesse[comm]);
                            return (
                                <tr key={`${settore}-${comm}`} className="amb-kpi-row-commessa">
                                    <td className="amb-kpi-cell-label">{comm}</td>
                                    {renderCells(commAgg, tData, subCols, formatters)}
                                </tr>
                            );
                        }) : []),
                    ];
                })}
                {globalKpi && (
                    <tr className="amb-kpi-row-totale">
                        <td className="amb-kpi-cell-label">Totale</td>
                        {renderCells(globalKpi, tData, subCols, formatters)}
                    </tr>
                )}
            </>
        );
    };

    // ── Configurazione colonne per le 2 tabelle ──

    // Tabella 1: Dettaglio Ore
    const oreSubCols = ['ore_tot', 'ore_lav', 'ore_pei', 'diff_perc'];
    const oreSubColLabels = ['Ore Tot', 'Ore Lav', 'Ore PEI', 'Diff [%]'];
    const oreFormatters = [fmtNum, fmtNum, fmtNum, fmtPerc];

    // Tabella 2: KPI per Settore
    const kpiSubCols = ['ore_tot', 'costi', 'costo_perc', 'ricavi'];
    const kpiSubColLabels = ['Ore Tot', 'Costi', 'Costo [%]', 'Ricavi'];
    const kpiFormatters = [fmtNum, fmtEuro, fmtPerc, fmtEuro];

    // ── Render ──

    if (loading) {
        return (
            <Lf23Loader />
        );
    }

    return (
        <div className="amb-gen-container">
            {/* ═══ TABELLA 1: Dettaglio Ore ═══ */}
            <div className={`amb-kpi-table-section${isFullscreenOre ? ' fullscreen-trend' : ''}`}>
                <RotateDevice active={isFullscreenOre} />
                <div className="amb-kpi-table-header">
                    <h3 className="trend-kpi-section-title">Dettaglio Ore</h3>
                    <div className="oper-hr-table-view-type-container">
                        <button type="button" className={`oper-hr-table-view-btn ${tableOreViewType === 'mese' ? 'active' : ''}`} onClick={() => setTableOreViewType('mese')} title="Vista Mensile"><span>M</span></button>
                        <button type="button" className={`oper-hr-table-view-btn ${tableOreViewType === 'trimestre' ? 'active' : ''}`} onClick={() => setTableOreViewType('trimestre')} title="Vista Trimestrale"><span>T</span></button>
                        <button type="button" className={`oper-hr-table-view-btn ${tableOreViewType === 'anno' ? 'active' : ''}`} onClick={() => setTableOreViewType('anno')} title="Vista Annuale"><span>A</span></button>
                        <button className="trend-fullscreen-btn" onClick={() => setIsFullscreenOre(!isFullscreenOre)} title={isFullscreenOre ? 'Esci da tutto schermo' : 'Tutto schermo'}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                {isFullscreenOre ? (<><polyline points="4 14 10 14 10 20"></polyline><polyline points="20 10 14 10 14 4"></polyline><line x1="14" y1="10" x2="21" y2="3"></line><line x1="3" y1="21" x2="10" y2="14"></line></>) : (<><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></>)}
                            </svg>
                        </button>
                    </div>
                </div>
                <div className="amb-kpi-table-wrapper">
                    <table className="amb-kpi-table">
                        <thead>
                            {renderTableHeaders(tableOreData, tableOreViewType, oreSubColLabels)}
                        </thead>
                        <tbody>
                            {renderTableBody(tableOreData, globalOreKpi, expandedOre, toggleOre, oreSubCols, oreFormatters)}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ═══ TABELLA 2: KPI per Settore ═══ */}
            <div className={`amb-kpi-table-section${isFullscreenKpi ? ' fullscreen-trend' : ''}`}>
                <RotateDevice active={isFullscreenKpi} />
                <div className="amb-kpi-table-header">
                    <h3 className="trend-kpi-section-title">KPI per Settore</h3>
                    <div className="oper-hr-table-view-type-container">
                        <button type="button" className={`oper-hr-table-view-btn ${tableKpiViewType === 'mese' ? 'active' : ''}`} onClick={() => setTableKpiViewType('mese')} title="Vista Mensile"><span>M</span></button>
                        <button type="button" className={`oper-hr-table-view-btn ${tableKpiViewType === 'trimestre' ? 'active' : ''}`} onClick={() => setTableKpiViewType('trimestre')} title="Vista Trimestrale"><span>T</span></button>
                        <button type="button" className={`oper-hr-table-view-btn ${tableKpiViewType === 'anno' ? 'active' : ''}`} onClick={() => setTableKpiViewType('anno')} title="Vista Annuale"><span>A</span></button>
                        <button className="trend-fullscreen-btn" onClick={() => setIsFullscreenKpi(!isFullscreenKpi)} title={isFullscreenKpi ? 'Esci da tutto schermo' : 'Tutto schermo'}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                {isFullscreenKpi ? (<><polyline points="4 14 10 14 10 20"></polyline><polyline points="20 10 14 10 14 4"></polyline><line x1="14" y1="10" x2="21" y2="3"></line><line x1="3" y1="21" x2="10" y2="14"></line></>) : (<><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></>)}
                            </svg>
                        </button>
                    </div>
                </div>
                <div className="amb-kpi-table-wrapper">
                    <table className="amb-kpi-table">
                        <thead>
                            {renderTableHeaders(tableKpiData, tableKpiViewType, kpiSubColLabels)}
                        </thead>
                        <tbody>
                            {renderTableBody(tableKpiData, globalKpiKpi, expandedKpi, toggleKpi, kpiSubCols, kpiFormatters)}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default Dettaglio;
